import React, { useCallback } from 'react';
import { dismissFlag as createDismissFlag, useFlagService, InfoFlag } from '@atlassian/jira-flags';

import {
	RequestAccessPromptFlag,
	type CrossJoinIntegraton,
	type CrossJoinTargetProduct,
} from '@atlassian/cross-join-nudges';

import {
	fireUIAnalytics,
	useAnalyticsEvents,
	type AnalyticsAttributes,
} from '@atlassian/jira-product-analytics-bridge';
import type { Collaborators } from '../../services/get-collaborators/types.tsx';
import { generateId } from '../../utils/index.tsx';

import { getMsrFeatureVisitedCount } from '../../services/msr/caching.tsx';

export const useMsrRequestAccessPromptFlag = () => {
	const { showFlag, dismissFlag } = useFlagService();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	return useCallback(
		async ({
			users,
			onSubmit,
			targetProduct,
			integration,
			eventAttributes,
		}: {
			users: Collaborators;
			onSubmit: () => Promise<void>;
			targetProduct: CrossJoinTargetProduct;
			integration: CrossJoinIntegraton;
			eventAttributes: AnalyticsAttributes;
		}) => {
			const id = `request-access-${generateId()}`;

			const dismissTheFlag = () => dismissFlag(createDismissFlag(id));

			const submit = async () => {
				fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'msrNudge', eventAttributes);
				dismissTheFlag();
				await onSubmit();
			};

			const dismiss = () => {
				fireUIAnalytics(createAnalyticsEvent({}), 'flag dismissed', 'msrNudge', eventAttributes);
				dismissTheFlag();
			};

			const dismissAndSnooze = () => {
				fireUIAnalytics(createAnalyticsEvent({}), 'flag snoozed', 'msrNudge', eventAttributes);
				dismissTheFlag();
			};

			fireUIAnalytics(createAnalyticsEvent({}), 'nudge viewed', 'msrNudge', eventAttributes);

			const featureVisitedCount = getMsrFeatureVisitedCount();

			showFlag({
				id,
				render: (props) => (
					<RequestAccessPromptFlag
						{...props}
						id={id}
						collaborators={users}
						onDismiss={dismiss}
						onSnoozeAndDismiss={dismissAndSnooze}
						onOptoutAndDismiss={dismissAndSnooze}
						onSubmit={submit}
						integration={integration}
						targetProduct={targetProduct}
						viewCount={featureVisitedCount}
						flagComponent={InfoFlag}
					/>
				),
			});
		},
		[createAnalyticsEvent, dismissFlag, showFlag],
	);
};

type CommonAnalyticsArgs = {
	users: Collaborators;
	userId: string;
	sessionId?: string;
	featureVisitedCount?: number;
	integration?: CrossJoinIntegraton;
};

export const getAnalyticsAttributes = ({
	users,
	userId,
	sessionId,
	integration,
}: CommonAnalyticsArgs): AnalyticsAttributes => {
	return {
		collaborators: users.length,
		userId,
		sessionId,
		integration,
	};
};
